import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { databaseService, PollData, SeasonWeek } from '../services/databaseService';
import { teamMappingService } from '../services/teamMappingService';

const PollDisplay: React.FC = () => {
  const [seasons, setSeasons] = useState<SeasonWeek[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [pollData, setPollData] = useState<{
    rankedTeams: PollData[];
    othersReceivingVotes: PollData[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const yearParam = urlParams.get('year');
    const weekParam = urlParams.get('week');
    
    if (yearParam && weekParam) {
      const year = parseInt(yearParam);
      const week = parseInt(weekParam);
      if (!isNaN(year) && !isNaN(week)) {
        setSelectedYear(year);
        setSelectedWeek(week);
      }
    }
  }, []);

  // Update URL when selections change
  useEffect(() => {
    if (selectedYear && selectedWeek) {
      const url = new URL(window.location.href);
      url.searchParams.set('year', selectedYear.toString());
      url.searchParams.set('week', selectedWeek.toString());
      window.history.replaceState({}, '', url.toString());
    }
  }, [selectedYear, selectedWeek]);

  useEffect(() => {
    loadSeasons();
    teamMappingService.initialize();
  }, []);

  useEffect(() => {
    if (selectedYear !== undefined && selectedYear !== null && selectedWeek !== undefined && selectedWeek !== null) {
      loadPollData(selectedYear, selectedWeek);
    }
  }, [selectedYear, selectedWeek]);

  const loadSeasons = async () => {
    try {
      setLoading(true);
      const availableSeasons = await databaseService.getAvailableSeasons();
      setSeasons(availableSeasons);
      
      // Only set defaults if no URL parameters are present
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams = urlParams.has('year') && urlParams.has('week');
      
      if (availableSeasons.length > 0 && !hasUrlParams) {
        setSelectedYear(availableSeasons[0].year); // Default to latest
        setSelectedWeek(availableSeasons[0].week);
      }
    } catch (err) {
      setError('Failed to load seasons');
      console.error('Error loading seasons:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPollData = async (year: number, week: number) => {
    try {
      setLoading(true);
      const data = await databaseService.getPollData(year, week);
      setPollData(data);
    } catch (err) {
      setError('Failed to load poll data');
      console.error('Error loading poll data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatWeek = (week: number): string => {
    return `Week ${week}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      // Parse the date string as local date to avoid timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleYearChange = (event: any) => {
    setSelectedYear(event.target.value);
  };

  const handleWeekChange = (event: any) => {
    setSelectedWeek(event.target.value);
  };

  const getAvailableYears = (): number[] => {
    return Array.from(new Set(seasons.map(s => s.year))).sort((a, b) => b - a);
  };

  const getAvailableWeeks = (year: number): number[] => {
    return seasons
      .filter(s => s.year === year)
      .map(s => s.week)
      .sort((a, b) => b - a);
  };

  const formatOthersReceivingVotes = (): string => {
    if (!pollData?.othersReceivingVotes.length) return '';
    
    const formatted = pollData.othersReceivingVotes
      .map(team => `${teamMappingService.getDisplayName(team.pollSchool)} ${team.pollPoints}`)
      .join(', ');
    
    return `Others Receiving Votes: ${formatted}`;
  };

  if (loading && !pollData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Season/Week Selection */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Season</InputLabel>
          <Select
            value={selectedYear || ''}
            onChange={handleYearChange}
            label="Season"
          >
            {getAvailableYears().map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Week</InputLabel>
          <Select
            value={selectedWeek !== null ? selectedWeek : ''}
            onChange={handleWeekChange}
            label="Week"
            disabled={!selectedYear}
          >
            {selectedYear && getAvailableWeeks(selectedYear).map((week) => (
              <MenuItem key={week} value={week}>
                {formatWeek(week)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Poll Date */}
      {pollData && pollData.rankedTeams.length > 0 && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {formatDate(pollData.rankedTeams[0].pollDate)}
          </Typography>
        </Box>
      )}

      {/* Poll Table */}
      {pollData && (
        <Box>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 1 }}>Rank</TableCell>
                  <TableCell sx={{ py: 1 }}>Team</TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pollData.rankedTeams.map((team) => (
                  <TableRow key={team.pollUniqueId} sx={{ '& td': { py: 0.5 } }}>
                    <TableCell component="th" scope="row">
                      {team.pollRank}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img
                          src={teamMappingService.getLogoUrl(team.pollSchool)}
                          alt={teamMappingService.getDisplayName(team.pollSchool)}
                          style={{ width: 30, height: 20 }}
                        />
                        <Typography variant="body1" component="div">
                          {teamMappingService.getDisplayName(team.pollSchool)}
                          {team.pollFpv && team.pollFpv > 0 && (
                            <Chip 
                              label={team.pollFpv} 
                              size="small" 
                              sx={{ ml: 1 }}
                              color="primary"
                            />
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {team.pollPoints}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Others Receiving Votes */}
          {pollData.othersReceivingVotes.length > 0 && (
            <Typography variant="body1" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
              {formatOthersReceivingVotes()}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PollDisplay;

